import { useCallback, useEffect, useMemo, useState } from 'react';
import { Skeleton } from 'antd';
import TabbedPage from 'components/tabbed-page';
import PageTitle from 'components/page-title';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import { colors } from 'theme/tokens';
import { Pill } from 'pages/support-shared/components';
import {
  FeaturePending,
  LoadFailed,
  TABULAR,
  isMissingEndpoint,
  unwrap,
} from 'pages/access-groups/common';
import { StaffContext, StaffCtx, staffError } from './shared';
import PendingTab from './pending-tab';
import ReviewTab from './review-tab';
import { ShiftOption, StaffAccessSummary } from './types';

/**
 * Trang "Xác nhận nhân viên" (/staff-confirm) — chỉ ADMIN (adminOnly trong nav-config).
 * docs/to-chuc-lai-nhom-quyen/00-ban-do-va-phuong-an-2026-09-29.md §5.2, §5.3.
 * Ba thẻ trên URL: ?tab=pending (mặc định) | review | ops.
 */

const TITLE = 'Xác nhận nhân viên';

type Phase = 'loading' | 'ready' | 'pending' | 'error';

const looksLikeSummary = (body: any) =>
  typeof body?.mode === 'string' && typeof body?.counts === 'object' && body.counts !== null;

/** Nhãn thẻ kèm số; ô số có bề rộng tối thiểu nên số đổi không làm thanh thẻ xê dịch. */
const CountLabel = ({ text, count, strong }: { text: string; count: number; strong?: boolean }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
    {text}
    <Pill
      color={strong && count > 0 ? colors.accentDeep : colors.textSecondary}
      bg={strong && count > 0 ? colors.accentSoft : colors.surfaceMuted}
      style={{
        ...TABULAR,
        minWidth: 26,
        justifyContent: 'center',
        padding: '0 7px',
        lineHeight: '18px',
      }}
    >
      {count > 999 ? '999+' : count}
    </Pill>
  </span>
);

const StaffConfirmPage = () => {
  const [phase, setPhase] = useState<Phase>('loading');
  const [summary, setSummary] = useState<StaffAccessSummary | null>(null);
  const [shifts, setShifts] = useState<ShiftOption[]>([]);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const loadSummary = useCallback(
    () =>
      AxiosUtils.get(API.staffAccessSummary).then((res: any) => {
        const next = unwrap<StaffAccessSummary>(res, looksLikeSummary);
        if (!next) throw new Error('Máy chủ trả về dữ liệu không đọc được. Vui lòng thử lại.');
        return next;
      }),
    []
  );

  useEffect(() => {
    let cancelled = false;
    loadSummary()
      .then((next) => {
        if (cancelled) return;
        setSummary(next);
        setPhase('ready');
      })
      .catch((err) => {
        if (cancelled) return;
        if (isMissingEndpoint(err)) {
          setPhase('pending');
          return;
        }
        setError(err instanceof Error ? err.message : staffError(err, 'Không tải được trang này.'));
        setPhase('error');
      });
    return () => {
      cancelled = true;
    };
  }, [attempt, loadSummary]);

  // Tên ca làm để hiện thay cho mã ca. Không tải được thì vẫn dùng trang bình thường.
  useEffect(() => {
    let cancelled = false;
    AxiosUtils.get(API.attendanceShifts)
      .then((res: any) => {
        if (cancelled) return;
        const body = res?.data;
        const list = Array.isArray(body)
          ? body
          : Array.isArray(body?.data)
          ? body.data
          : Array.isArray(body?.items)
          ? body.items
          : [];
        setShifts(list);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const afterAction = useCallback(() => {
    setRefreshKey((n) => n + 1);
    loadSummary()
      .then(setSummary)
      .catch(() => undefined);
  }, [loadSummary]);

  const ctx: StaffCtx | null = useMemo(
    () =>
      summary
        ? {
            summary,
            shifts,
            refreshKey,
            afterAction,
            applySummary: setSummary,
            settingsOpen,
            setSettingsOpen,
          }
        : null,
    [summary, shifts, refreshKey, afterAction, settingsOpen]
  );

  const retry = () => {
    setPhase('loading');
    setAttempt((n) => n + 1);
  };

  if (phase !== 'ready' || !ctx || !summary) {
    return (
      <div>
        <PageTitle title={TITLE} />
        {phase === 'loading' && (
          <div aria-busy="true" aria-label="Đang tải">
            <Skeleton active title={{ width: 360 }} paragraph={{ rows: 8 }} />
          </div>
        )}
        {phase === 'pending' && <FeaturePending onRetry={retry} />}
        {(phase === 'error' || (phase === 'ready' && !ctx)) && (
          <LoadFailed
            title="Không tải được trang Xác nhận nhân viên"
            text={error}
            onRetry={retry}
          />
        )}
      </div>
    );
  }

  const counts = summary.counts || { pending: 0, needsReview: 0, opsManagers: 0 };

  return (
    <StaffContext.Provider value={ctx}>
      <TabbedPage
        title={TITLE}
        tabs={[
          {
            key: 'pending',
            label: <CountLabel text="Chờ duyệt" count={counts.pending || 0} strong />,
            render: () => <PendingTab />,
          },
          {
            key: 'review',
            label: <CountLabel text="Cần rà soát" count={counts.needsReview || 0} strong />,
            render: () => <ReviewTab key="LEGACY" kind="LEGACY" />,
          },
          {
            key: 'ops',
            label: <CountLabel text="Quản lý vận hành" count={counts.opsManagers || 0} />,
            render: () => <ReviewTab key="OPS" kind="OPS" />,
          },
        ]}
      />
    </StaffContext.Provider>
  );
};

export default StaffConfirmPage;
